import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import authFetch from '../utils/authFetch';
import { NAVY, fmtQty, sourceRowLabel } from '../styles/ds';

const fmtCost = v => v == null ? '—' : `₪${Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`;

const rowLabel = (r) => {
  if (r.source_type === 'production' && r.production_batch_id) {
    return `מנת ייצור #${r.production_batch_id}`;
  }
  return sourceRowLabel(r);
};

/**
 * Read-only inventory breakdown for one variant by source (supplier /
 * production batch / opening / adjustment), computed live from
 * inventory_batches. Production batches appear as individual rows.
 */
const SupplierInventoryBreakdown = ({ variantId, unit }) => {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!variantId) return;
    authFetch(`/variants/${variantId}/supplier-inventory`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setData({ rows: [], totalAvailable: 0 }));
  }, [variantId]);

  useEffect(() => { load(); }, [load]);

  if (data === null) return <Typography fontSize={12} color="text.secondary">טוען מלאי לפי מקור...</Typography>;

  return (
    <Box>
      <Typography fontWeight={700} fontSize={13} color={NAVY} mb={1}>מלאי לפי מקור</Typography>
      {data.rows.length === 0 ? (
        <Typography fontSize={12} color="text.secondary">אין מלאי זמין למוצר זה</Typography>
      ) : (
        <Box data-mobile-scroll sx={{ overflowX: 'auto', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.08)' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: NAVY }}>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>מקור</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }} align="center">זמין</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }} align="center">משוריין</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }} align="center">זמין בפועל</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#fff' }} align="right">עלות ממוצעת ₪</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((r, i) => {
                const isProdBatch = r.source_type === 'production' && r.production_batch_id;
                return (
                  <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#faf8f5' }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {rowLabel(r)}
                        {isProdBatch && (
                          <Tooltip title={`פתח מנת ייצור #${r.production_batch_id}`} arrow>
                            <IconButton size="small" sx={{ p: 0.2 }}
                              onClick={() => window.open(`/#/production_batches/${r.production_batch_id}/show`, '_blank')}>
                              <OpenInNewIcon sx={{ fontSize: 13, color: '#888' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {r.source_type === 'supplier' && variantId && (
                          <Tooltip title="הצג רכישות מספק זה" arrow>
                            <IconButton size="small" sx={{ p: 0.2 }}
                              onClick={() => window.open(
                                `/#/purchases?filter=${encodeURIComponent(JSON.stringify({ variant_id: variantId, supplier_id: r.supplier_id }))}`,
                                '_blank'
                              )}>
                              <OpenInNewIcon sx={{ fontSize: 13, color: '#888' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!r.selectable && (
                          <Chip label="לא ניתן לבחירה ידנית" size="small"
                            sx={{ height: 16, fontSize: 9, backgroundColor: '#F8D7DA', color: '#721C24' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12 }}>{fmtQty(r.available_qty, unit)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: 12, color: r.reserved_qty > 0 ? '#856404' : 'text.secondary' }}>
                      {r.reserved_qty > 0 ? fmtQty(r.reserved_qty, unit) : '—'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12, fontWeight: 700, color: '#155724' }}>
                      {fmtQty(r.available_after_reservation, unit)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 12, color: 'text.secondary' }}>{fmtCost(r.unit_cost_ils)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: 'rgba(13,29,62,0.06)' }}>
                <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>סה"כ מלאי</TableCell>
                <TableCell align="center" sx={{ fontSize: 12, fontWeight: 700 }}>{fmtQty(data.totalAvailable, unit)}</TableCell>
                <TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default SupplierInventoryBreakdown;
